import { execFileSync } from "node:child_process";
import { readFileSync } from "node:fs";
import { relative } from "node:path";
import type { SavingsReport } from "./types.js";
import { findScaffoldFiles } from "./drift/index.js";
import { estimateTokens, stripFrontmatter } from "./utils/fs.js";
import { readQueries } from "./telemetry/query-log.js";
import { runBench } from "./bench.js";
import { extractSummary } from "./mcp/section-index.js";
import { parseRtkDiscover } from "./suggest/rtk.js";

/**
 * Compute the three token-savings estimates. Each part is independent and
 * best-effort — a failure in one (no telemetry, no rtk binary) leaves the
 * others intact and just marks that part unavailable. Deterministic except for
 * the optional `rtk discover` subprocess.
 */
export function computeSavings(projectRoot: string, scaffoldRoot: string): SavingsReport {
  return {
    context: contextSavings(projectRoot, scaffoldRoot),
    bench: benchSavings(projectRoot, scaffoldRoot),
    rtk: rtkSavings(projectRoot),
  };
}

/**
 * Savings from serving partial context instead of whole files.
 *
 * Measured (preferred): once Claude has queried the MCP server, sum
 * `fullFileTokens − tokens` over every logged read — real cumulative savings.
 *
 * Projected (fresh checkout, no telemetry yet): forecast one default
 * summary-mode read of each scaffold file (`full − summary`), so the headline
 * is a real, honest number from the start instead of zero. Switches to the
 * measured figure as soon as any query is logged.
 */
function contextSavings(projectRoot: string, scaffoldRoot: string): SavingsReport["context"] {
  // Full + summary token counts for every current scaffold file, by rel path.
  const fullTokens = new Map<string, number>();
  let projectedSaved = 0;
  for (const abs of findScaffoldFiles(projectRoot, scaffoldRoot)) {
    const rel = relative(projectRoot, abs);
    try {
      const stripped = stripFrontmatter(readFileSync(abs, "utf-8"));
      const full = estimateTokens(stripped);
      fullTokens.set(rel, full);
      projectedSaved += Math.max(0, full - estimateTokens(extractSummary(stripped)));
    } catch {
      // unreadable file — skip; contributes 0 to both measured and projected
    }
  }

  const queries = readQueries(projectRoot).filter((q) => q.file && typeof q.tokens === "number");
  if (queries.length > 0) {
    let saved = 0;
    for (const q of queries) {
      const full = fullTokens.get(q.file as string);
      if (full === undefined) continue; // file referenced in log no longer exists
      saved += Math.max(0, full - (q.tokens ?? 0));
    }
    return { available: true, savedTokens: saved, queries: queries.length, projected: false };
  }

  return {
    available: projectedSaved > 0,
    savedTokens: projectedSaved,
    queries: 0,
    projected: true,
  };
}

/**
 * Per-turn projection from the deterministic router benchmark: the median
 * tokens saved per task vs. the flat "load everything" baseline. Honest framing
 * — it's a projection of standing context, not measured end-to-end usage.
 */
function benchSavings(projectRoot: string, scaffoldRoot: string): SavingsReport["bench"] {
  try {
    const result = runBench(projectRoot, scaffoldRoot);
    if (result.scaffoldFiles === 0 || result.tasks.length === 0) {
      return { available: false, savedPerTurn: 0, savedPct: 0 };
    }
    const savings = result.tasks.map((t) => t.savedTokens).sort((a, b) => a - b);
    const medianSaved = savings[Math.floor((savings.length - 1) / 2)];
    return {
      available: true,
      savedPerTurn: Math.max(0, medianSaved),
      savedPct: Math.max(0, result.medianSavedPct),
    };
  } catch {
    return { available: false, savedPerTurn: 0, savedPct: 0 };
  }
}

/**
 * Shell-command savings reported by the external `rtk discover` tool. Best-
 * effort: bounded timeout, and any failure (binary absent, non-zero exit,
 * unparseable output) marks it unavailable rather than throwing.
 */
function rtkSavings(projectRoot: string): SavingsReport["rtk"] {
  try {
    const out = execFileSync("rtk", ["discover"], {
      cwd: projectRoot,
      encoding: "utf-8",
      timeout: 4000,
      stdio: ["ignore", "pipe", "ignore"],
    });
    const findings = parseRtkDiscover(out);
    if (findings.length === 0) return { available: false, savedTokens: 0, commands: 0 };
    const saved = findings.reduce((sum, f) => sum + f.estSaving, 0);
    return { available: true, savedTokens: saved, commands: findings.length };
  } catch {
    return { available: false, savedTokens: 0, commands: 0 };
  }
}
